import React, { useState, useEffect, useCallback, useRef } from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { Card, CardBody, Container } from "reactstrap";
import { useTheme, useMediaQuery, Box } from "@mui/material";

import Joyride, { Step } from "react-joyride";
import { useInteractiveTour } from "../../hooks/useInteractiveTour";
import JessModal from "../../components/JessModal";
import TourAnimatedCursor from "../../components/TourAnimatedCursor";
import BackgroundImage from "../../assets/backgrounds/Background.svg";

// hooks
import { useRedux } from "../../hooks/index";
import { useProfile } from "../../hooks/UserHooks";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useSessionManagement } from "../../hooks/chat/useSessionManagement";
import { DashboardThemeProvider } from "./context/DashboardThemeContext";
import { useGlobalTheme } from "../../context/GlobalThemeContext";

// components
import StatusPage from "./Profile/StatusPage";
import JessPage from "./Profile/JessPage";
import JessToggleButton from "../../components/JessToggleButton";
import DashboardSkeleton from "./DashboardSkeleton";
import { ResponsiveNavbar } from "../../components/ResponsiveNavbar/ResponsiveNavbar";
import ThemeToggle from "../../components/ThemeToggle";
import LanguageSelector from "../../components/LanguageSelector";
import { STATUS } from "../../constants";
import { Session } from "./types";
import api from "../../utils/api";
import { fetchAgentConnectionStatus } from "../../api/agent";
import jessProfileIcon from "../../assets/navbar/iconperfiljesss.svg";
import jessProfileIconOff from "../../assets/navbar/iconperfiljesssOff.svg";
import jessNameLogo from "../../assets/navbar/nomejesss.svg";
import logger from "../../utils/logger";

const Index: React.FC = () => {
  const { useAppSelector } = useRedux();
  const { t } = useTranslation();
  const { userProfile: profileFromHook } = useProfile();
  const { isMobile, isTablet } = useBreakpoint();
  // Show navbar when screen is below 1024px (mobile or tablet)
  const shouldShowNavbar = isMobile || isTablet;
  const errorData = (state: any) => ({
    selectedChat: state.Chats.selectedChat,
    userProfile: state.Profile.userProfile,
  });
  const { selectedChat, userProfile } = useAppSelector(errorData);
  const [isAgentEnabled, setIsAgentEnabled] = useState<boolean>(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
  const isDarkTheme = useGlobalTheme();

  // ✅ Session management with background pre-fetch
  const { syncingSessions, setActiveSessionData, activeSessionData } =
    useSessionManagement(profileFromHook, null);

  const connectANewWhatsappAccountButtonRef = useRef<HTMLButtonElement>(null);

  const [runTour, setRunTour] = useState(false);

  // Hook do tour interativo
  const {
    isRunning: isTourRunning,
    currentStep,
    steps: tourSteps,
    isWaitingForInteraction,
    totalSteps,
    stopTour,
    nextStep: tourNextStep,
    previousStep: tourPreviousStep,
    skipTour,
    canGoNext,
    canGoBack,
    getCurrentTarget,
    handleJoyrideCallback: handleInteractiveTourCallback,
  } = useInteractiveTour({
    userId: profileFromHook?.userName || "anonymous",
    onTourComplete: () => {},
    onTourSkip: () => {},
    config: {
      autoStart: false,
      locale: "pt-BR",
    },
  });

  const [steps, setSteps] = useState<Step[]>([
    // Example:
    // {
    //   target: '.tab-jess',
    //   content: t("tourJoyride.jessArea"),
    //   disableBeacon: true
    // },
  ]);

  useEffect(() => {
    const newSteps = [...steps];

    if (
      !steps.some(
        (step) => step.target === connectANewWhatsappAccountButtonRef.current,
      )
    ) {
      newSteps.unshift({
        target: connectANewWhatsappAccountButtonRef.current as HTMLElement,
        content: t("tourJoyride.whatsappButtonAdd"),
        disableBeacon: true,
      });
    }

    if (newSteps.length > steps.length) {
      setSteps(newSteps);
    }
  }, [connectANewWhatsappAccountButtonRef, steps, t]);

  useEffect(() => {
    const hasSeenTour = Cookies.get("has_seen_tour");

    const fetchStatus = async () => {
      try {
        const [sessionResponse, agentStatusResult] = await Promise.allSettled([
          api.get("/api/get-status"),
          fetchAgentConnectionStatus(),
        ]);

        // Process sessions (critical path - always show even if agent fails)
        if (sessionResponse.status === "fulfilled") {
          const result = sessionResponse.value.data;
          const sessions = Array.isArray(result.session_status)
            ? result.session_status.map((session: any) => ({
                ...session,
                phoneNumber: session.phone_number || "",
                belongs_to_ai:
                  session.belongs_to_ai === true ||
                  session.belongs_to_ai === "true",
              }))
            : [];

          setSessions(sessions);

          // Show content immediately after sessions load
          setLoading(false);
          setShowSkeleton(false);

          if (!hasSeenTour && sessions.length <= 1) {
            setRunTour(true);
          }
        } else {
          logger.error(
            "[Dashboard] Failed to fetch sessions:",
            sessionResponse.reason,
          );
          setSessions([]);
          setLoading(false);
          setShowSkeleton(false);
        }

        if (agentStatusResult.status === "fulfilled") {
          setIsAgentEnabled(agentStatusResult.value.isAgentConnected);
        } else {
          logger.warn("[Dashboard] Agent status unavailable (non-critical)");
          setIsAgentEnabled(false);
        }
      } catch (error) {
        logger.error("[Dashboard] Unexpected error:", error);
        setLoading(false);
        setShowSkeleton(false);
      }
    };

    fetchStatus();
  }, []);

  const activeTour = () => {
    setRunTour(true);
  };

  const userRole = userProfile?.role || "admin";

  const updateSessions = useCallback(async () => {
    try {
      const response = await api.get("/api/get-status");
      const result = response.data;

      if (result.session_status && Array.isArray(result.session_status)) {
        // Ensure belongs_to_ai is properly set as boolean
        const normalizedSessions = result.session_status.map(
          (session: any) => ({
            ...session,
            belongs_to_ai:
              session.belongs_to_ai === true ||
              session.belongs_to_ai === "true",
          }),
        );

        setSessions(normalizedSessions);

        // Auto-select new WORKING user session as active
        const userSessions = normalizedSessions.filter(
          (s: any) => s.belongs_to_ai !== true,
        );
        const workingSessions = userSessions.filter(
          (s: any) => s.status === "WORKING",
        );

        if (workingSessions.length > 0 && !activeSessionData) {
          const newSession = workingSessions[0];
          const sessionData = {
            id: newSession.id,
            channel: newSession.channel,
            name: newSession.name,
          };
          setActiveSessionData(sessionData);
        }
      } else {
        setSessions([]);
      }
    } catch (error) {
      logger.error("[Dashboard] Failed to update sessions:", error);
    }
  }, []);

  // Filter sessions to separate Jesss from customer sessions
  const jessSession = sessions.find(
    (session) => session.belongs_to_ai === true,
  );
  const customersSessions = sessions.filter(
    (session) => session.belongs_to_ai !== true,
  );

  const determineJessStatus = (
    isAgentEnabled: boolean,
    jessSession: Session,
    otherSessions: Session[],
  ): STATUS => {
    const hasWorkingSession = otherSessions.some(
      (session) => session.status === "WORKING",
    );
    const hasOtherSessions = otherSessions.length > 0;

    if (jessSession.status === "STOPPED") {
      return STATUS.Offline;
    }

    if (!isAgentEnabled) {
      return STATUS.OpenAgent;
    }

    if (isAgentEnabled) {
      if (hasOtherSessions && hasWorkingSession) {
        return STATUS.Online;
      }
      return STATUS.ConectWhatsapp;
    }

    // Fallback case: should not occur if all conditions above are handled
    return STATUS.ConectWhatsapp;
  };

  const jessStatus = jessSession
    ? determineJessStatus(isAgentEnabled, jessSession, customersSessions)
    : STATUS.Offline; // Default if jessSession is unexpectedly null.
  const jessPhoneNumber = jessSession?.phone_number || "";

  const loadingControl = (value: boolean) => {
    setLoading(value);
  };

  const handleThemeToggle = () => {
    const newTheme = !isDarkTheme;
    localStorage.setItem("theme", newTheme ? "dark" : "light");
    window.dispatchEvent(
      new CustomEvent("appThemeChange", { detail: { isDark: newTheme } }),
    );
  };

  // Agent connection status para o ResponsiveNavbar
  const agentConnectionStatus = {
    isAgentConnected: isAgentEnabled,
    companyId: userProfile?.companyId || "",
    timestamp: Date.now(),
  };

  if (loading && showSkeleton) {
    return <DashboardSkeleton />;
  }

  return (
    <DashboardThemeProvider value={isDarkTheme}>
      <>
        {/* Responsive Navbar - Show when screen width is below 1024px (mobile or tablet) */}
        {shouldShowNavbar && (
          <ResponsiveNavbar
            isConnected={isAgentEnabled}
            connection={{
              status: isAgentEnabled ? "connected" : "disconnected",
              message: "",
            }}
            isDarkTheme={isDarkTheme}
            onThemeToggle={handleThemeToggle}
            profileImage={
              agentConnectionStatus?.isAgentConnected
                ? jessProfileIcon
                : jessProfileIconOff
            }
            profileName={jessNameLogo}
            agentConnectionStatus={agentConnectionStatus}
            syncingSessions={syncingSessions}
          />
        )}

        {/* Jess + Theme + Language buttons - desktop only (top-right) */}
        {!shouldShowNavbar && (
          <Box
            sx={{
              position: "fixed",
              top: 16,
              right: 16,
              display: "flex",
              gap: 1,
              zIndex: 9999,
            }}
          >
            <JessToggleButton isDarkTheme={isDarkTheme} />
            <ThemeToggle
              isDarkTheme={isDarkTheme}
              onToggle={handleThemeToggle}
            />
            <LanguageSelector isDarkTheme={isDarkTheme} />
          </Box>
        )}

        {/* Estilos CSS para o Tour */}
        <style>{`
        @keyframes spotlightPulse {
          0%, 100% {
            box-shadow: 
              0 0 40px rgba(131, 222, 128, 0.8),
              0 0 80px rgba(131, 222, 128, 0.4),
              inset 0 0 20px rgba(131, 222, 128, 0.2);
            border: 3px solid rgba(131, 222, 128, 0.6);
          }
          50% {
            box-shadow: 
              0 0 60px rgba(131, 222, 128, 1),
              0 0 120px rgba(131, 222, 128, 0.6),
              inset 0 0 30px rgba(131, 222, 128, 0.3);
            border: 3px solid rgba(131, 222, 128, 0.9);
          }
        }

        /* Garantir que o spotlight seja visível */
        .__floater__body {
          display: none !important;
        }
        
        /* Forçar o spotlight a aparecer corretamente */
        [data-tour-elem="spotlight"] {
          border-radius: 16px !important;
          box-shadow: 
            0 0 40px rgba(131, 222, 128, 0.8),
            0 0 80px rgba(131, 222, 128, 0.4),
            inset 0 0 20px rgba(131, 222, 128, 0.2) !important;
          border: 3px solid rgba(131, 222, 128, 0.6) !important;
          animation: spotlightPulse 3s ease-in-out infinite !important;
        }

        /* Garantir que o botão QR Code seja destacado */
        #dashboard-qr-code-button {
          position: relative !important;
          z-index: 10001 !important;
        }
      `}</style>

        {/* Tour Interativo */}
        <Joyride
          steps={tourSteps}
          run={isTourRunning}
          continuous
          scrollToFirstStep
          showProgress={false}
          showSkipButton={false}
          disableOverlayClose={true}
          disableOverlay={false}
          callback={handleInteractiveTourCallback}
          debug={true}
          key={`tour-${currentStep}`}
          styles={{
            options: {
              primaryColor: "#4C3699",
              zIndex: 10000,
              arrowColor: "transparent",
            },
            overlay: {
              background: "rgba(0, 0, 0, 0.1)",
              backdropFilter: "none",
            },
            spotlight: {
              borderRadius: "16px",
              boxShadow: `
              0 0 40px rgba(131, 222, 128, 0.8),
              0 0 80px rgba(131, 222, 128, 0.4),
              inset 0 0 20px rgba(131, 222, 128, 0.2)
            `,
              border: "3px solid rgba(131, 222, 128, 0.6)",
              animation: "spotlightPulse 3s ease-in-out infinite",
              overflow: "auto",
              pointerEvents: "auto",
            },
            tooltip: {
              display: "none",
            },
            tooltipContainer: {
              display: "none",
            },
            tooltipTitle: {
              display: "none",
            },
            tooltipContent: {
              display: "none",
            },
            buttonNext: {
              display: "none",
            },
            buttonBack: {
              display: "none",
            },
            buttonSkip: {
              display: "none",
            },
            buttonClose: {
              display: "none",
            },
          }}
          locale={{
            back: t("tourJoyride.Back"),
            close: t("tourJoyride.Close"),
            last: t("tourJoyride.Last"),
            next: t("tourJoyride.Next"),
            skip: t("tourJoyride.Skip"),
          }}
        />

        {/* Modal da Jess para o Tour */}
        <JessModal
          isOpen={isTourRunning}
          currentStep={currentStep}
          stepIndex={currentStep}
          steps={tourSteps}
          totalSteps={totalSteps}
          isWaitingForInteraction={isWaitingForInteraction}
          canGoNext={canGoNext}
          canGoBack={canGoBack}
          onNext={tourNextStep}
          onBack={tourPreviousStep}
          onSkip={skipTour}
          onClose={stopTour}
          targetSelector={getCurrentTarget()}
        />

        {/* Cursor Animado do Tour - Não mostrar no Step 3 */}
        <TourAnimatedCursor
          targetSelector={getCurrentTarget()}
          isVisible={isTourRunning && currentStep !== 2}
          isWaitingForInteraction={isWaitingForInteraction}
          onActionCompleted={() => {}}
        />
        <div
          className={classnames(
            "user-chat",
            "w-100",
            "h-100",
            "overflow-x-hidden",
            {
              "user-chat-show": selectedChat,
            },
          )}
          id="user-chat"
          style={{
            backgroundColor: isDarkTheme ? "#141414" : "#f7f7f7",
            backgroundImage: isDarkTheme ? "none" : `url(${BackgroundImage})`,
            backgroundPosition: "center bottom",
            backgroundRepeat: "no-repeat",
            backgroundSize: "100% auto",
            position: "relative",
          }}
        >
          <div className="user-chat-overlay" id="user-chat-overlay"></div>

          <div className="max-card-area">
            <Container
              style={{
                maxWidth: "50em",
                marginTop:
                  customersSessions.length === 0
                    ? isMobile
                      ? "7em"
                      : "6em"
                    : isMobile
                      ? "9em"
                      : "8em",
                marginBottom: customersSessions.length === 0 ? "2em" : "0",
              }}
              className="card-status d-flex flex-column"
            >
              <Card
                className="big-card container-card"
                style={
                  isDarkTheme
                    ? {
                        backgroundColor: "rgba(20, 20, 20, 0.98)",
                        border: "1px solid rgba(255,255,255,0.08)",
                      }
                    : {}
                }
              >
                <CardBody
                  style={isDarkTheme ? { backgroundColor: "transparent" } : {}}
                >
                  <JessPage
                    jessPhoneNumber={jessPhoneNumber}
                    statusJess={jessStatus}
                  />
                  <StatusPage
                    ref={connectANewWhatsappAccountButtonRef}
                    sessions={customersSessions}
                    role={userRole}
                    updateSessions={updateSessions}
                    loadingSearch={loading}
                    loadingControl={loadingControl}
                    activeTour={activeTour}
                    isAgentEnabled={isAgentEnabled}
                  />
                </CardBody>
              </Card>
            </Container>
          </div>
        </div>
      </>
    </DashboardThemeProvider>
  );
};

export default Index;
